import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';

import type { PipelineFunnel } from '@control-center/shared';

import { STAGE_LABELS } from '../../../lib/deal-helpers';
import { formatPipelineMoney, formatPct } from '../../../lib/pipeline-helpers';

// Hex resolvidos pra Recharts (não aceita CSS var) — match dos design tokens.
// Uma cor principal (brand) pro funil; estágio terminal positivo usa o tom "good".
// Sem gradiente (proibição 03), sem sombra colorida (proibição), máx 2 cores de estado.
const BRAND = '#7c3aed'; // var(--brand)
const GOOD = '#16a34a'; // var(--good) — fechamento_ganho
const MUTED = '#a1a1aa'; // var(--muted)

interface FunnelChartProps {
  funnel: PipelineFunnel;
  /** Quando false (role sem valores), oculta valores monetários no tooltip. */
  showMoney: boolean;
}

interface FunnelDatum {
  stage: string;
  label: string;
  count: number;
  value: number | null;
  conversion: number | null;
  isWon: boolean;
}

/**
 * Funil de pipeline (Story 11.12 AC3) — barras horizontais por estágio (ordem
 * fixa DR-039), com contagem, valor e taxa de conversão entre estágios.
 * Recharts dentro dos tokens do design system.
 */
export function FunnelChart({ funnel, showMoney }: FunnelChartProps): JSX.Element {
  const data: FunnelDatum[] = funnel.stages.map((s) => ({
    stage: s.stage,
    label: STAGE_LABELS[s.stage],
    count: s.count,
    value: s.value,
    conversion: s.conversion_from_previous,
    isWon: s.stage === 'fechamento_ganho',
  }));

  const isEmpty = funnel.total_count === 0;

  return (
    <section className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">Funil de pipeline</h3>
        <div className="text-xs text-muted">
          {funnel.total_count} {funnel.total_count === 1 ? 'deal' : 'deals'} em aberto
          {showMoney && funnel.total_value !== null && (
            <span className="ml-2 tabular-nums text-muted-2">
              · {formatPipelineMoney(funnel.total_value)}
            </span>
          )}
        </div>
      </header>

      {isEmpty ? (
        <div className="py-10 text-center text-sm text-muted">
          Sem deals em aberto no pipeline com os filtros atuais.
        </div>
      ) : (
        <div className="h-80 w-full p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={data}
              margin={{ top: 4, right: 48, bottom: 4, left: 8 }}
            >
              <XAxis type="number" stroke={MUTED} fontSize={11} allowDecimals={false} />
              <YAxis dataKey="label" type="category" stroke={MUTED} fontSize={12} width={130} />
              <Tooltip
                content={<FunnelTooltip showMoney={showMoney} />}
                cursor={{ fill: 'rgba(124, 58, 237, 0.08)' }}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {data.map((d) => (
                  <Cell key={d.stage} fill={d.isWon ? GOOD : BRAND} />
                ))}
                <LabelList dataKey="count" position="right" fontSize={11} fill={MUTED} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Taxa de conversão entre estágios — lida tabular abaixo do gráfico (AC3). */}
      {!isEmpty && (
        <div className="border-t border-line px-4 py-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
            {data
              .filter((d) => d.conversion !== null)
              .map((d) => (
                <div key={d.stage} className="flex items-center justify-between text-xs">
                  <span className="truncate text-muted">→ {d.label}</span>
                  <span className="tabular-nums font-medium text-ink">
                    {formatPct(d.conversion)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </section>
  );
}

function FunnelTooltip({
  active,
  payload,
  showMoney,
}: TooltipProps<number, string> & { showMoney: boolean }): JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0]?.payload as FunnelDatum;
  return (
    <div className="rounded-md border border-line bg-panel px-3 py-2 text-xs shadow-md">
      <div className="font-medium text-ink">{item.label}</div>
      <div className="mt-1 tabular-nums text-ink-2">
        {item.count} {item.count === 1 ? 'deal' : 'deals'}
      </div>
      {showMoney && item.value !== null && (
        <div className="tabular-nums text-muted">{formatPipelineMoney(item.value)}</div>
      )}
      {item.conversion !== null && (
        <div className="text-muted">
          Conversão do estágio anterior: {formatPct(item.conversion)}
        </div>
      )}
    </div>
  );
}
